/**
 * SYR-107 — the read-out.
 *
 * This is the payoff of a five-minute wait and the only thing most visitors
 * will ever see of the product, so it is built as a verdict rather than a
 * table. Three deliberate decisions:
 *
 * 1. THE ANSWER LEADS. The constrained element is the largest type on the
 *    page, above the headline that explains it. Someone who reads four words
 *    should still leave knowing what we think.
 * 2. EVIDENCE NOTES GET FULL WIDTH. They were in a third column at ~240px,
 *    which ran them to six or seven lines and made the scorecard look like a
 *    spreadsheet. Each row is now name + signal on one line, reasoning
 *    beneath it at full measure.
 * 3. THE CONSTRAINT ROW IS UNMISTAKABLE — accent rail, tinted ground, its own
 *    label. Row order still matches the model's, which is the same order as
 *    the dashboard's Boosters card and the tease email, so a brand that later
 *    signs up meets the same sequence.
 *
 * Visual language is the marketer view's: Card radius/shadow, Eyebrow, and
 * BAND_PALETTE for the signal colours, so this reads as the same product as
 * the dashboard rather than a microsite.
 *
 * Must hold up at BOTH widths — inside a ~680px iframe on brandscience.ai and
 * full-page from the emailed link.
 */

import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PlayCircleFilled } from '@ant-design/icons';
import { BAND_PALETTE } from '../../components/MarketerView/config/bandColours';
import { THEME } from '../../components/MarketerView/config/theme';
import { Eyebrow } from '../../components/MarketerView/atoms';
import {
    IPublicHypothesisElementReading,
    IPublicHypothesisResult,
} from '../../services/nestjs-base-api/types';

// The constraint gets its own colour rather than a band colour. It is not
// "bad" the way a weak score is bad — it is the ANSWER, and it should read as
// the conclusion of the page, not as an alarm.
const CONSTRAINT = {
    fg: '#7c3aed',
    bg: '#f5f3ff',
    line: '#ddd6fe',
};

const SIGNAL: Record<
    IPublicHypothesisElementReading['signal'],
    { fg: string; bg: string; line: string; messageId: string; label: string }
> = {
    LIKELY_CONSTRAINT: {
        ...CONSTRAINT,
        messageId: 'marketerView.hypothesis.signal.likelyConstraint',
        label: 'Likely constraint',
    },
    STRENGTH: {
        ...BAND_PALETTE.strong,
        messageId: 'marketerView.hypothesis.signal.strength',
        label: 'Looks strong',
    },
    WATCH: {
        ...BAND_PALETTE.watch,
        messageId: 'marketerView.hypothesis.signal.watch',
        label: 'Worth watching',
    },
    INSUFFICIENT_SIGNAL: {
        ...BAND_PALETTE.na,
        messageId: 'marketerView.hypothesis.signal.insufficient',
        label: "Can't tell yet",
    },
};

interface Props {
    result: IPublicHypothesisResult;
    /** Where the CTA goes when the Charter intake is shut. The stable front
     *  door: it redirects to the Charter application if the intake is open, so
     *  an old emailed link is never wrong. */
    signUpUrl: string;
    /** Charter intake open, read fresh on every view rather than stamped into
     *  the read-out. Swaps the closing copy and sends the CTA to the cohort
     *  page instead. */
    charterOpen?: boolean;
    /** Opens the explainer for an element. The page decides HOW — in a modal
     *  when standalone, or by asking the hosting page when embedded. */
    onPlayVideo?: (videoId: string, title: string, element: string) => void;
    /** Fired as the reader leaves. Must never block or delay the navigation —
     *  the link works whether or not it lands. */
    onCtaClick?: () => void;
    /** SYR-109: self-serve signup is open, so a Charter-open read-out also
     *  offers the standard-pricing door as a second, quieter line. */
    publicSignupEnabled?: boolean;
    /** SYR-109: with the Free tier withdrawn, no line here may say "free". */
    freeTierSignupEnabled?: boolean;
}

// The cohort page on the website, not the application form in this app: someone
// who has just finished a free read-out is being asked to consider the offer,
// not to fill in an eight-step form. Absolute because it is another host.
const CHARTER_URL = 'https://brandscience.ai/charter';

export const ResultView: React.FC<Props> = ({
    result,
    signUpUrl,
    charterOpen,
    publicSignupEnabled,
    freeTierSignupEnabled,
    onCtaClick,
    onPlayVideo,
}) => {
    const intl = useIntl();
    const readings = result.elementReadings ?? [];
    const competitors = result.competitorsAnalysed ?? [];

    return (
        <div>
            {/* Above everything, because it changes how the page below it
                should be read. Someone opening a link from an email that named
                a different element deserves to be told we looked again, not
                left to decide which of the two we meant. */}
            {result.rerunNotice ? (
                <div
                    style={{
                        background: '#fff8e6',
                        border: '1px solid #ffe08a',
                        borderRadius: 12,
                        padding: '14px 18px',
                        marginBottom: 20,
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: THEME.textBase,
                    }}
                >
                    <FormattedMessage
                        id="publicHypothesis.result.rerunNotice"
                        defaultMessage="We analysed {brand} again since we emailed you, with more evidence than the first pass had. The read-out below is the current one, and it now points at {current} rather than {previous}."
                        values={{
                            brand: result.brandName ?? 'your brand',
                            current: <strong>{result.constraintElement}</strong>,
                            previous: <strong>{result.rerunNotice.previousElement}</strong>,
                        }}
                    />
                </div>
            ) : null}

            {/* ---------------------------------------------- the verdict */}
            <section
                style={{
                    background: THEME.bgCard,
                    border: `1px solid ${THEME.border}`,
                    borderRadius: 16,
                    boxShadow: THEME.cardShadow,
                    padding: 0,
                    overflow: 'hidden',
                    marginBottom: 28,
                }}
            >
                {/* A single accent band across the top. Cheap, and it stops the
                    card reading as a plain white box. */}
                <div
                    style={{
                        height: 4,
                        background: `linear-gradient(90deg, ${CONSTRAINT.fg} 0%, ${THEME.primary} 100%)`,
                    }}
                />
                <div style={{ padding: '26px 28px 28px' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            flexWrap: 'wrap',
                            marginBottom: 20,
                        }}
                    >
                        {/* Names the brand up front. This page is opened days
                            later from an email, and without it the read-out
                            never actually says whose brand it is about. */}
                        <Eyebrow>
                            {result.brandName ? (
                                <FormattedMessage
                                    id="publicHypothesis.result.eyebrowFor"
                                    defaultMessage="The Hypothesis for {brand}"
                                    values={{ brand: result.brandName }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="publicHypothesis.result.eyebrow"
                                    defaultMessage="The Hypothesis"
                                />
                            )}
                        </Eyebrow>
                        {/* The inference disclaimer sits in the FIRST thing a
                            reader's eye lands on, not in a footnote. It is what
                            makes this a way in to the paid product rather than
                            a weaker competitor to it. */}
                        <span
                            style={{
                                display: 'inline-block',
                                background: CONSTRAINT.bg,
                                color: CONSTRAINT.fg,
                                border: `1px solid ${CONSTRAINT.line}`,
                                borderRadius: 999,
                                padding: '4px 12px',
                                fontSize: 11.5,
                                fontWeight: 700,
                                letterSpacing: '0.01em',
                            }}
                        >
                            <FormattedMessage
                                id="publicHypothesis.result.inferredBadge"
                                defaultMessage="A brand equity perspective inferred from public signals, not from your customers. So, probably not quite right."
                            />
                        </span>
                    </div>

                    {result.constraintElement ? (
                        <div
                            style={{
                                fontSize: 'clamp(30px, 5vw, 40px)',
                                lineHeight: 1.08,
                                fontWeight: 800,
                                letterSpacing: '-0.022em',
                                color: CONSTRAINT.fg,
                                marginBottom: 10,
                            }}
                        >
                            {result.constraintElement}
                        </div>
                    ) : null}

                    <h1
                        style={{
                            fontSize: 'clamp(17px, 2.4vw, 20px)',
                            lineHeight: 1.35,
                            fontWeight: 700,
                            letterSpacing: '-0.01em',
                            color: THEME.textBase,
                            margin: '0 0 18px',
                        }}
                    >
                        {result.headline}
                    </h1>

                    <p
                        style={{
                            fontSize: 15.5,
                            lineHeight: 1.68,
                            color: '#334155',
                            margin: 0,
                            maxWidth: '62ch',
                        }}
                    >
                        {result.claim}
                    </p>
                </div>
            </section>

            {/* ------------------------------------------- the scorecard */}
            {readings.length > 0 ? (
                <section style={{ marginBottom: 28 }}>
                    {/* Its own keys, not the marketer view's shared
                        scorecardTitle: a signed-in customer already knows the
                        vocabulary, a stranger is meeting it for the first
                        time. Naming the Boosters here is what lets them
                        recognise the same six drivers if they ever reach a
                        filled dashboard. Deliberately no count — the dashboard
                        says "The 7 Brand Boosters" and this shows the six that
                        can be measured from customers. */}
                    <Eyebrow style={{ marginBottom: 8 }}>
                        <FormattedMessage
                            id="publicHypothesis.result.scorecardTitle"
                            defaultMessage="How your brand reads across the Brand Boosters"
                        />
                    </Eyebrow>
                    <p
                        style={{
                            fontSize: 13.5,
                            lineHeight: 1.6,
                            color: THEME.textSecondary,
                            margin: '0 0 12px',
                        }}
                    >
                        <FormattedMessage
                            id="publicHypothesis.result.scorecardBlurb"
                            defaultMessage="The drivers we measure once a brand starts collecting from its own customers."
                        />
                    </p>

                    {/* auto-fit, not a media query: inline styles cannot carry
                        one, and the container is a different width standing
                        alone (1040) than inside the /hypothesis iframe. Two-up
                        wherever two 340px cards fit, single column on mobile
                        and in narrow frames, with no code knowing which. */}
                    <div
                        style={{
                            display: 'grid',
                            // min(100%, …): a bare 340px minimum forces
                            // horizontal scroll on a 360px phone, where the
                            // content box is ~320px.
                            gridTemplateColumns:
                                'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
                            gap: 10,
                            alignItems: 'stretch',
                        }}
                    >
                        {readings.map((reading) => {
                            const tone = SIGNAL[reading.signal];
                            const isConstraint = reading.signal === 'LIKELY_CONSTRAINT';
                            return (
                                <article
                                    key={reading.element}
                                    style={{
                                        position: 'relative',
                                        // Deliberately NOT spanning the
                                        // constraint across the row: with six
                                        // readings the grid is even, and a
                                        // full-width card strands whichever
                                        // reading shares its row next to empty
                                        // cells. The purple fill and the rail
                                        // are what make it findable.
                                        background: isConstraint ? tone.bg : THEME.bgCard,
                                        border: `1px solid ${isConstraint ? tone.line : THEME.border}`,
                                        borderRadius: 14,
                                        // The accent rail is what makes the
                                        // constraint findable at a glance in a
                                        // list of five.
                                        borderLeft: isConstraint
                                            ? `4px solid ${tone.fg}`
                                            : `1px solid ${THEME.border}`,
                                        padding: isConstraint ? '16px 18px 17px' : '14px 18px 15px',
                                        boxShadow: isConstraint ? THEME.cardShadow : 'none',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            justifyContent: 'space-between',
                                            gap: 12,
                                            flexWrap: 'wrap',
                                            marginBottom: 7,
                                        }}
                                    >
                                        <h3
                                            style={{
                                                fontSize: isConstraint ? 17 : 15.5,
                                                fontWeight: isConstraint ? 800 : 700,
                                                letterSpacing: '-0.01em',
                                                color: THEME.textBase,
                                                margin: 0,
                                            }}
                                        >
                                            {reading.element}
                                        </h3>
                                        <span
                                            style={{
                                                flex: '0 0 auto',
                                                fontSize: 11.5,
                                                fontWeight: 700,
                                                color: tone.fg,
                                                background: isConstraint ? '#fff' : tone.bg,
                                                border: `1px solid ${tone.line}`,
                                                borderRadius: 999,
                                                padding: '3px 11px',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {intl.formatMessage({
                                                id: tone.messageId,
                                                defaultMessage: tone.label,
                                            })}
                                        </span>
                                    </div>
                                    {/* Full width, not a third column. This is
                                        the reasoning, and it was being squeezed
                                        into six lines of a narrow gutter. */}
                                    <p
                                        style={{
                                            fontSize: 14,
                                            lineHeight: 1.62,
                                            color: THEME.textSecondary,
                                            margin: 0,
                                            maxWidth: '68ch',
                                        }}
                                    >
                                        {reading.note}
                                    </p>
                                    {/* Only where an explainer exists. The
                                        library is being built one element at a
                                        time, so most rows will not have one and
                                        an empty affordance would be worse than
                                        none. */}
                                    {reading.videoId && onPlayVideo ? (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                onPlayVideo(
                                                    reading.videoId!,
                                                    // Server-resolved, so the
                                                    // wording is Steve's to
                                                    // change without a deploy.
                                                    reading.videoTitle || reading.element,
                                                    reading.element,
                                                )
                                            }
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 7,
                                                marginTop: 11,
                                                padding: 0,
                                                border: 0,
                                                background: 'none',
                                                cursor: 'pointer',
                                                color: isConstraint ? tone.fg : THEME.primaryHover,
                                                fontSize: 13.5,
                                                fontWeight: 700,
                                                fontFamily: 'inherit',
                                            }}
                                        >
                                            <PlayCircleFilled style={{ fontSize: 16 }} />
                                            <FormattedMessage
                                                id="publicHypothesis.result.watchExplainer"
                                                defaultMessage="What is {element}?"
                                                values={{ element: reading.element }}
                                            />
                                        </button>
                                    ) : null}
                                </article>
                            );
                        })}
                    </div>
                </section>
            ) : null}

            {/* --------------------------------------------- what we read */}
            <section
                style={{
                    background: THEME.bgTinted,
                    border: `1px solid ${THEME.borderSubtle}`,
                    borderRadius: 14,
                    padding: '16px 18px',
                    marginBottom: 28,
                }}
            >
                <Eyebrow style={{ marginBottom: 8 }}>
                    <FormattedMessage
                        id="publicHypothesis.result.readEyebrow"
                        defaultMessage="What we read"
                    />
                </Eyebrow>
                {result.sourcesSummary ? (
                    <p
                        style={{
                            fontSize: 13.5,
                            lineHeight: 1.6,
                            color: THEME.textSecondary,
                            margin: 0,
                        }}
                    >
                        {result.sourcesSummary}
                    </p>
                ) : null}
                {competitors.length > 0 ? (
                    <p
                        style={{
                            fontSize: 13.5,
                            lineHeight: 1.6,
                            color: THEME.textSecondary,
                            margin: '6px 0 0',
                        }}
                    >
                        <FormattedMessage
                            id="marketerView.hypothesis.competitors"
                            defaultMessage="Competitors analysed: {names}"
                            values={{
                                names: (
                                    <span style={{ color: THEME.textBase, fontWeight: 600 }}>
                                        {competitors.join(', ')}
                                    </span>
                                ),
                            }}
                        />
                    </p>
                ) : null}
            </section>

            {/* ---------------------------------------- the closing question */}
            {result.closingQuestion ? (
                <blockquote
                    style={{
                        margin: '0 0 28px',
                        padding: '2px 0 2px 20px',
                        borderLeft: `3px solid ${CONSTRAINT.fg}`,
                    }}
                >
                    {/* The bridge to the CTA below. Without it the strongest
                        line on the page floats unlabelled, and the reader is
                        left to work out that the next block is how you answer
                        it. */}
                    <p
                        style={{
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: THEME.textMuted,
                            margin: '0 0 8px',
                        }}
                    >
                        <FormattedMessage
                            id="publicHypothesis.result.questionKicker"
                            defaultMessage="The question only your customers can answer"
                        />
                    </p>
                    <p
                        style={{
                            fontSize: 'clamp(16px, 2.2vw, 18px)',
                            lineHeight: 1.5,
                            fontWeight: 600,
                            letterSpacing: '-0.01em',
                            color: THEME.textBase,
                            margin: 0,
                        }}
                    >
                        {result.closingQuestion}
                    </p>
                </blockquote>
            ) : null}

            {/* --------------------------------------------------- the CTA */}
            <section
                style={{
                    background: THEME.textBase,
                    borderRadius: 16,
                    padding: '26px 28px',
                    textAlign: 'center',
                }}
            >
                <p
                    style={{
                        fontSize: 15,
                        lineHeight: 1.65,
                        color: '#CBD5E1',
                        margin: '0 auto 18px',
                        maxWidth: '46ch',
                    }}
                >
                    {charterOpen ? (
                        <FormattedMessage
                            id="publicHypothesis.result.ctaBlurbCharter"
                            defaultMessage="This is a hypothesis, and hypotheses are often wrong. We read your brand from the outside, and the only way to find out for sure is to hear it from the people who actually buy from you. The Charter cohort is six brands we do exactly that with, one-to-one, and it is open now."
                        />
                    ) : (
                        <FormattedMessage
                            id="publicHypothesis.result.ctaBlurb"
                            defaultMessage="This is a hypothesis, and hypotheses are often wrong. We read your brand from the outside, and the only way to find out for sure is to hear it from the people who actually buy from you."
                        />
                    )}
                </p>
                {/* Flat primary, never a gradient — matches the Briefing's
                    primary button. */}
                <a
                    href={charterOpen ? CHARTER_URL : signUpUrl}
                    target="_top"
                    onClick={onCtaClick}
                    style={{
                        display: 'inline-block',
                        padding: '13px 30px',
                        background: THEME.primary,
                        color: '#fff',
                        textDecoration: 'none',
                        borderRadius: 8,
                        fontWeight: 700,
                        fontSize: 15.5,
                        letterSpacing: '-0.01em',
                        boxShadow: '0 4px 12px rgba(245, 158, 11, .32)',
                    }}
                >
                    {charterOpen ? (
                        <FormattedMessage
                            id="publicHypothesis.result.ctaCharter"
                            defaultMessage="See the Charter cohort"
                        />
                    ) : (
                        <FormattedMessage
                            id="publicHypothesis.result.cta"
                            defaultMessage="Find out if we're right"
                        />
                    )}
                </a>
                {/* The same scale-and-price line /hypothesis carries, so a
                    reader cannot tell from one page and not the other whether
                    this is a $500 tool or a $50k engagement. */}
                <div
                    style={{
                        marginTop: 14,
                        fontSize: 12.5,
                        lineHeight: 1.6,
                        color: '#94A3B8',
                    }}
                >
                    {charterOpen ? (
                        <FormattedMessage
                            id="publicHypothesis.result.ctaNoteCharter"
                            defaultMessage="$6,600 USD for the first year, a third off the standard $9,900. Six places, so we can work with each one."
                        />
                    ) : freeTierSignupEnabled === false ? (
                        <FormattedMessage
                            id="publicHypothesis.result.ctaNoteNoFree"
                            defaultMessage="Professional is $990 USD a month, or $9,900 a year."
                        />
                    ) : (
                        <FormattedMessage
                            id="publicHypothesis.result.ctaNote"
                            defaultMessage="Start free. Professional is $990 USD a month, or $9,900 a year."
                        />
                    )}
                </div>
                {charterOpen && publicSignupEnabled && (
                    /* SYR-109: both doors are open, so both are offered.
                       Charter keeps the button; standard pricing is one quiet
                       line, and either click counts as the CTA click. */
                    <div style={{ marginTop: 10, fontSize: 13.5 }}>
                        <a
                            href={signUpUrl}
                            target="_top"
                            onClick={onCtaClick}
                            style={{
                                color: '#FCD34D',
                                textDecoration: 'underline',
                                textUnderlineOffset: 3,
                            }}
                        >
                            <FormattedMessage
                                id="publicHypothesis.result.ctaSecondary"
                                defaultMessage="or start on standard pricing"
                            />
                        </a>
                    </div>
                )}
                {result.generatedAt ? (
                    <div
                        style={{
                            marginTop: 16,
                            fontSize: 12,
                            color: THEME.textMuted,
                        }}
                    >
                        <FormattedMessage
                            id="publicHypothesis.result.generatedAt"
                            defaultMessage="Worked out on {date}"
                            values={{
                                date: new Date(result.generatedAt).toLocaleDateString(),
                            }}
                        />
                    </div>
                ) : null}
            </section>
        </div>
    );
};
